import { Alert, Dialog, Stack } from "@tm/components"
import { FormProvider, useForm } from "react-hook-form"
import { useEffect, useMemo, useRef, useState } from "react"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { RepairTimeProvider, WorkCategory, WorkType } from "@tm/models"
import { DialogCustomRepairTime } from "@bundles/compilations/components/compilations/OwnRepairTimes/RepairTimeEditDialog"
import { useLocalization } from "@tm/localization"
import { CustomerDefaults } from "../../../../../../data/model"
import { BasicBasketWorkInfo, BasketWork } from "../../../../../../models"
import { EditDialogHeader } from "./EditDialogHeader"
import WorkDialogFields from "./WorkDialogFields"
import { useRepairTimesFormDefaults } from "../../../../../../hooks/Forms/useRepairTimesFormDefaults"
import { EditWorkMode } from "../../../../business/model"
import { useSchema } from "./schema"
import { OwnWorkFields } from "./OwnWorkFields"
import { useSaveOwnWork } from "./hooks/useSaveOwnWork"
import { useMapOwnWorkRequest } from "./hooks/useMapOwnWorkRequest"

type DialogWorkAdd = { type: "add" }
type DialogWorkEdit = { type: "edit"; work: BasketWork }
export type DialogWork = DialogWorkAdd | DialogWorkEdit
export type OwnWorkRequest = Omit<DialogCustomRepairTime, "type">

type Props = {
    customerDefaults?: CustomerDefaults
    repairTimeDivision: number
    showRepairTimesInHours: boolean
    workInfo: DialogWork
    disableHeaderButtons?: boolean
    onCloseButtonClick(): void
    onAddCustomWork?(editBasketWorkInfo: BasicBasketWorkInfo): void
    onEditWork?(editBasketWorkInfo: BasicBasketWorkInfo, editWorkMode?: EditWorkMode): void
}

export function WorkEditDialog(props: Props) {
    const {
        workInfo,
        customerDefaults,
        showRepairTimesInHours,
        repairTimeDivision,
        disableHeaderButtons,
        onCloseButtonClick,
        onAddCustomWork,
        onEditWork,
    } = props
    const [saveError, setSaveError] = useState<string | undefined>()
    const [hasFixedPrice, setHasFixedPrice] = useState(workInfo.type === "edit" && !!workInfo.work.calculatedWork?.isFixedPrice)
    const { vatRateItems, workCategoryItems } = useRepairTimesFormDefaults()
    const workNumberRequired = useMemo(() => {
        let isRequired = false
        if (workInfo.type === "edit") {
            isRequired = workInfo.work.workItem?.provider !== undefined && workInfo.work.workItem.provider !== RepairTimeProvider.Eurotax
        }
        return isRequired
    }, [workInfo])

    const { translateText } = useLocalization()
    const firstOpened = useRef(true)
    const saveOwnWork = useSaveOwnWork()
    const schema = useSchema(hasFixedPrice, workNumberRequired)
    const mapOwnWorkRequest = useMapOwnWorkRequest(hasFixedPrice, workNumberRequired, vatRateItems)

    const defaultValues = useMemo(() => {
        if (workInfo.type === "add") {
            const defaultCategory = WorkCategory.WorkshopWork
            return {
                saveAsOwnWork: false,
                workNumber: "",
                description: "",
                category: defaultCategory,
                regularPrice: 0,
                surcharge: customerDefaults?.worksSurcharge ?? 0,
                time: 0,
                fixedPriceValue: hasFixedPrice ? 0 : undefined,
                hourlyRate: workCategoryItems.find((x) => x.category === defaultCategory)?.hourlyRate ?? 0,
                rebate: customerDefaults?.worksRebate ?? 0,
                offerPrice: 0,
                vatType: vatRateItems?.first()?.vatRate.vatType ?? 0,
                calculatedPrice: 0,
            }
        }

        const { calculatedWork, workItem, estimatedWork } = workInfo.work || {}
        const { vatRates, displayNumber, category, providerWorkId } = workItem
        return {
            workNumber: displayNumber ?? providerWorkId ?? "",
            description: estimatedWork?.description ?? workItem?.description ?? "",
            category: category ?? WorkCategory.WorkshopWork,
            surcharge: calculatedWork?.surcharge ?? workItem?.surcharge ?? 0,
            time: calculatedWork?.repairTime ?? estimatedWork?.displayTime?.value ?? 0,
            fixedPriceValue: hasFixedPrice ? (calculatedWork?.regularPrice?.value ?? 0) : undefined,
            hourlyRate: calculatedWork?.hourlyRate?.value ?? workItem?.hourlyRate ?? 0,
            rebate: calculatedWork?.rebate ?? workItem?.rebate ?? 0,
            offerPrice: calculatedWork?.offerPrice?.value ?? 0,
            vatType: vatRates?.find((vatRate) => vatRate.isSelected)?.vatType ?? 1,
            calculatedPrice: calculatedWork?.offerPrice?.value,
        }
    }, [workInfo, workCategoryItems, vatRateItems, customerDefaults, hasFixedPrice])

    type WorkFormData = z.infer<typeof schema>

    const form = useForm<WorkFormData>({
        resolver: zodResolver(schema),
        defaultValues,
    })
    const { handleSubmit, watch, setValue, resetField } = form

    const selectedCategory = watch("category")
    const vatType = watch("vatType")
    const saveAsOwnWork = watch("saveAsOwnWork")

    // When default VatRates has loaded and the user didn't select a value, set the default
    useEffect(() => {
        if (vatRateItems.length > 0 && vatType === 0) {
            resetField("vatType", { defaultValue: vatRateItems[0].vatRate.vatType })
        }
    }, [vatRateItems])

    // When changing the work category, the hourly rate will be changed according to the selected category
    useEffect(() => {
        if (!firstOpened.current) {
            setValue("hourlyRate", workCategoryItems.find((x) => x.category === selectedCategory)?.hourlyRate ?? 0)
        }
        firstOpened.current = false
    }, [selectedCategory, setValue, workCategoryItems])

    async function handleConfirmButtonClick(data: WorkFormData) {
        const { vatType, category, workNumber, description, fixedPriceValue, hourlyRate, rebate, surcharge, time } = data
        const vatRate = vatRateItems.find((vr) => vr.vatRate.vatType === vatType)?.vatRate
        const categoryOfWork: WorkCategory = (category as number) ?? WorkCategory.WorkshopWork
        let success: boolean = false
        if (!vatRate) {
            return
        }
        const editBasketWorkInfo: BasicBasketWorkInfo = {
            workNumber: workNumber as string,
            categoryOfWork,
            description: description as string,
            rebate: rebate as number,
            time: time as number,
            fixedPriceValue: fixedPriceValue ? (fixedPriceValue as number) : undefined,
            hourlyRate: hourlyRate ? (hourlyRate as number) : undefined,
            surcharge: surcharge as number,
            vatRate: {
                vatRate: vatRate.vatValue,
                vatType: vatRate.vatType,
            },
        }
        if (workInfo.type === "add") {
            onAddCustomWork?.(editBasketWorkInfo)

            if (saveAsOwnWork) {
                const request = mapOwnWorkRequest(data)
                success = await saveOwnWork(request)
                setSaveError(success ? undefined : translateText(13452))
            }
        } else {
            const { workItem } = workInfo.work
            let editWorkMode
            if (
                workItem.type !== WorkType.CustomWork &&
                ((workItem.displayNumber ?? workItem.providerWorkId) !== workNumber ||
                    workItem.description !== description ||
                    workItem.category !== category)
            ) {
                editWorkMode = EditWorkMode.replaceWorkWithCustomWork
            } else if (workItem.type === WorkType.CustomWork && (workNumber || description)) {
                editWorkMode = EditWorkMode.editCustomWork
            } else if (workNumber || description) {
                editWorkMode = EditWorkMode.editRepairTime
            }

            onEditWork?.(editBasketWorkInfo, editWorkMode)
        }

        if (success || !saveAsOwnWork) {
            onCloseButtonClick()
        }
    }

    return (
        <Dialog open position="middle" fullWidth maxWidth="lg">
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(handleConfirmButtonClick)}>
                    <Stack gap={2}>
                        <EditDialogHeader
                            isSaving={disableHeaderButtons}
                            work={workInfo.type === "edit" ? workInfo.work : undefined}
                            onCloseButtonClick={onCloseButtonClick}
                        />
                        {saveError && <Alert size="small" severity="error" title={saveError} variant="outlined" sx={{ mb: 2 }} />}
                        <WorkDialogFields
                            work={workInfo.type === "edit" ? workInfo.work : undefined}
                            hasFixedPrice={hasFixedPrice}
                            workNumberRequired={workNumberRequired}
                            repairTimeDivision={repairTimeDivision}
                            showRepairTimesInHours={showRepairTimesInHours}
                            onChangeHasFixedPrice={() => setHasFixedPrice(!hasFixedPrice)}
                        />
                        {workInfo.type === "add" && (
                            <OwnWorkFields hasFixedPrice={hasFixedPrice} workNumberRequired={workNumberRequired} onSaveError={setSaveError} />
                        )}
                    </Stack>
                </form>
            </FormProvider>
        </Dialog>
    )
}
